import React, { useState, useEffect } from 'react';

const BookingCalendar = () => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [initialized, setInitialized] = useState(false);

    const courts = [
        { id: 1, name: 'Court 1' },
    ];

    // Get dates for current week
    const getWeekDates = (date) => {
        const curr = new Date(date);
        const week = [];
        
        // Get Monday of current week
        const day = curr.getDay();
        const diff = curr.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is Sunday
        curr.setDate(diff);
        
        
        // Get next 7 days
        for (let i = 0; i < 7; i++) {
            week.push(new Date(curr));
            curr.setDate(curr.getDate() + 1);
        }
        
        return week;
    };

    const timeSlots = [];
    let hour = 10; // Start at 10am
    let minutes = 0;

    while (hour < 20 || (hour === 20 && minutes === 0)) {
        timeSlots.push(`${hour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`);
        minutes += 60;
        if (minutes >= 60) {
            hour += 1;
            minutes -= 60;
        }
    }

    const formatDateForAPI = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const previousWeek = () => {
        setCurrentDate(prev => {
            const newDate = new Date(prev);
            newDate.setDate(newDate.getDate() - 7);
            return newDate;
        });
    };

    const nextWeek = () => {
        setCurrentDate(prev => {
            const newDate = new Date(prev);
            newDate.setDate(newDate.getDate() + 7);
            return newDate;
        });
    };

    const isDateBeforeToday = (date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return date < today;
    };

    const getUserBookingsForDate = (date) => {
        return bookings.filter(booking => 
            booking.booking_date === formatDateForAPI(date) && 
            booking.user_id === booking.current_user_id
        );
    };

    const canUserBook = (date) => {
        if (isDateBeforeToday(date)) {
            return false;
        }
        const userBookings = getUserBookingsForDate(date);
        return userBookings.length < 2;
    };

    const fetchBookings = async (date) => {
        try {
            const formattedDate = formatDateForAPI(date);
            const response = await fetch(`/wp-json/booking-calendar/v1/bookings?date=${formattedDate}`, {
                headers: {
                    'X-WP-Nonce': bookingCalendarData.nonce
                }
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const text = await response.text();
            const data = JSON.parse(text);
            return Array.isArray(data) ? data : [];
        } catch (error) {
            console.error('Error fetching bookings:', error);
            return [];
        }
    };

    const handleTimeSlotClick = async (courtId, timeSlot, date) => {
        if (isDateBeforeToday(date)) {
            alert("Cannot book slots in the past");
            return;
        }

        const userBookings = getUserBookingsForDate(date);
        if (userBookings.length >= 2) {
            alert("You can only book 2 slots per day");
            return;
        }

        setLoading(true);

        const [hours, minutes] = timeSlot.split(':').map(num => parseInt(num));
        let endHours = hours;
        let endMinutes = minutes + 45;
        
        if (endMinutes >= 60) {
            endHours += 1;
            endMinutes -= 60;
        }

        const endTime = `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;

        const bookingData = {
            date: formatDateForAPI(date),
            startTime: timeSlot,
            endTime: endTime,
            courtId: courtId
        };

        try {
            const response = await fetch('/wp-json/booking-calendar/v1/bookings', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': bookingCalendarData.nonce
                },
                body: JSON.stringify(bookingData)
            });

            const result = await response.json();

            if (response.ok) {
                setBookings(prevBookings => {
                    const newBooking = {
                        ...result,
                        court_id: parseInt(courtId),
                        start_time: timeSlot,
                        booking_date: formatDateForAPI(date),
                        user_name: result.user_name,
                        current_user_id: result.user_id
                    };
                    return [...prevBookings, newBooking];
                });
            } else {
                throw new Error(result.message || 'Booking failed');
            }
        } catch (error) {
            console.error('Booking error:', error);
            alert('Failed to create booking. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteBooking = async (bookingId) => {
        if (!window.confirm('Are you sure you want to delete this booking?')) {
            return;
        }
    
        setLoading(true);
        try {
            const response = await fetch(`/wp-json/booking-calendar/v1/bookings/${bookingId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': bookingCalendarData.nonce
                }
            });
    
            if (response.ok) {
                setBookings(prevBookings => prevBookings.filter(b => b.id !== bookingId));
            } else {
                const error = await response.json();
                throw new Error(error.message || 'Failed to delete booking');
            }
        } catch (error) {
            console.error('Error deleting booking:', error);
            alert(error.message || 'Failed to delete booking');
        } finally {
            setLoading(false);
        }
    };

    const getBookingForSlot = (courtId, timeSlot, date) => {
        return bookings.find(b => 
            parseInt(b.court_id) === parseInt(courtId) &&
            b.start_time === timeSlot &&
            b.booking_date === formatDateForAPI(date)
        );
    };

    // Fetch bookings for the week when current date changes
    useEffect(() => {
        const fetchAllBookings = async () => {
            setLoading(true);
            const weekDates = getWeekDates(currentDate);
            try {
                const allBookings = await Promise.all(
                    weekDates.map(async (date) => {
                        const formattedDate = formatDateForAPI(date);
                        const response = await fetch(`/wp-json/booking-calendar/v1/bookings?date=${formattedDate}`, {
                            headers: {
                                'X-WP-Nonce': bookingCalendarData.nonce
                            }
                        });
                        
                        if (!response.ok) {
                            throw new Error(`HTTP error! status: ${response.status}`);
                        }
                        const text = await response.text();
                        return JSON.parse(text);
                    })
                );

                // Combine all bookings into a single array
                const combinedBookings = allBookings.flat().filter(booking => booking);
                setBookings(combinedBookings);
            } catch (error) {
                console.error('Error fetching bookings:', error);
                setBookings([]);
            } finally {
                setLoading(false);
                setInitialized(true);
            }
        };

        fetchAllBookings();
    }, [currentDate]);

    const weekDates = getWeekDates(currentDate);

    return (
        <div className="booking-calendar">
            <div className="calendar-header">
                <button onClick={previousWeek}>&lt; Previous Week</button>
                <h2>{`Week of ${currentDate.toLocaleDateString('en-US', { 
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                })}`}</h2>
                <button onClick={nextWeek}>Next Week &gt;</button>
            </div>

            <div className="courts-grid">
                <div className="time-header">
                    <div className="corner-cell">Time</div>
                    {weekDates.map(date => (
                        <div key={date.toISOString()} className="court-header">
                            {date.toLocaleDateString('en-US', { 
                                weekday: 'short',
                                month: 'short',
                                day: 'numeric'
                            })}
                        </div>
                    ))}
                </div>
                {timeSlots.map(timeSlot => (
                    <div key={timeSlot} className="time-row">
                        <div className="time-cell">{timeSlot}</div>
                        {weekDates.map(date => {
                            const booking = getBookingForSlot(courts[0].id, timeSlot, date);
                            const isUsersBooking = booking ? booking.user_id === booking.current_user_id : false;
                            return (
                                <div key={`${date.toISOString()}-${timeSlot}`} className="booking-cell">
                                    {booking ? (
                                        <div className="booked-slot" title={`Booked by ${booking.user_name}`}>
                                            {booking.user_name || 'Booked'}
                                            {isUsersBooking && (
                                                <button 
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteBooking(booking.id);
                                                    }}
                                                    className="delete-booking"
                                                    title="Delete booking"
                                                >
                                                    ×
                                                </button>
                                            )}
                                        </div>
                                    ) : (
                                        <button
                                            className="booking-slot available"
                                            onClick={() => handleTimeSlotClick(courts[0].id, timeSlot, date)}
                                            disabled={loading || !canUserBook(date)}
                                            title={!canUserBook(date) ? "Maximum 2 bookings per day" : ""}
                                        >
                                            Book
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>

                            {loading && !initialized && (
                <div className="loading-overlay">
                    Loading...
                </div>
            )}
        </div>
    );
};

export default BookingCalendar;